// src/features/inventory/pages/SupplierDetailPage/index.jsx
import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSuppliers } from '../../hooks/useSuppliers';
import { useInventorySupplierPermissions } from '../../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../../hooks/mui/useConfirmDialog';
import SupplierHeader from './SupplierHeader';
import SupplierTabs from './tabs';
import { useNavigationStack } from '../../../../context/NavigationStackContext';

export default function SupplierDetailPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canUpdate, canDelete } = useInventorySupplierPermissions();

  const {
    currentSupplier,
    loading,
    error,
    clearError,
    fetchSupplierById,
    updateSupplier,
    deleteSupplier,
  } = useSuppliers();

  useEffect(() => {
    fetchSupplierById(supplierId);
  }, [fetchSupplierById, supplierId]);

  const handleToggleActive = useCallback(async () => {
    if (!canUpdate || !currentSupplier) return;
    const result = await updateSupplier(supplierId, { active: !currentSupplier.active });
    if (!result.success) showError(result.error);
  }, [canUpdate, currentSupplier, supplierId, updateSupplier, showError]);

  const handleDelete = useCallback(() => {
    showConfirmDialog({
      title: t('inventory:suppliers.confirm.delete_title'),
      message: t('inventory:suppliers.confirm.delete_message', { name: currentSupplier?.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteSupplier(supplierId);
        if (result.success) {
          showSuccess(t('inventory:suppliers.success.deleted'));
          navigateBack('..', { replace: true });
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteSupplier, supplierId, currentSupplier, showSuccess, showError, navigateBack, t]);

  if (loading && !currentSupplier) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!currentSupplier) return null;

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <SupplierHeader
        supplier={currentSupplier}
        canUpdate={canUpdate}
        canDelete={canDelete}
        onToggleActive={handleToggleActive}
        onEdit={() => navigate('edit')}
        onDelete={handleDelete}
        onBack={() => navigateBack('..')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      <SupplierTabs supplier={currentSupplier} />

      <ConfirmDialog />
    </Box>
  );
}
